import _extends from '@babel/runtime/helpers/extends';
import { StyleSheet } from 'aphrodite';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import * as React from 'react';
import { Text } from '@khanacademy/wonder-blocks-core';

const Regular = 400;
const Bold = 700;
const Black = 900;
const mobile = "@media (max-width: 1023px)";
const desktop = "@media (min-width: 1024px)";
const common = {
  display: "block"
};
const SansFamily = 'Lato, "Noto Sans", sans-serif';
const SerifFamily = '"Noto Serif", serif';
const InconsolataFamily = "Inconsolata, monospace";
const styles = StyleSheet.create({
  Title: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Black,
    [desktop]: {
      fontSize: 36,
      lineHeight: "40px"
    },
    [mobile]: {
      fontSize: 28,
      lineHeight: "32px"
    }
  }),
  Tagline: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Regular,
    fontSize: 20,
    lineHeight: "24px"
  }),
  HeadingLarge: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Bold,
    [desktop]: {
      fontSize: 28,
      lineHeight: "32px"
    },
    [mobile]: {
      fontSize: 24,
      lineHeight: "28px"
    }
  }),
  HeadingMedium: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Bold,
    [desktop]: {
      fontSize: 24,
      lineHeight: "28px"
    },
    [mobile]: {
      fontSize: 22,
      lineHeight: "26px"
    }
  }),
  HeadingSmall: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Bold,
    fontSize: 20,
    lineHeight: "24px"
  }),
  HeadingXSmall: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Bold,
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: 0.6,
    textTransform: "uppercase"
  }),
  BodySerifBlock: _extends({}, common, {
    fontFamily: SerifFamily,
    fontWeight: Regular,
    fontSize: 22,
    lineHeight: "28px"
  }),
  BodySerif: _extends({}, common, {
    fontFamily: SerifFamily,
    fontWeight: Regular,
    fontSize: 18,
    lineHeight: "22px"
  }),
  BodyMonospace: _extends({}, common, {
    fontFamily: InconsolataFamily,
    fontWeight: Regular,
    fontSize: 17,
    lineHeight: "22px"
  }),
  Body: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Regular,
    fontSize: 16,
    lineHeight: "22px"
  }),
  LabelLarge: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Bold,
    fontSize: 16,
    lineHeight: "20px"
  }),
  LabelMedium: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Regular,
    fontSize: 16,
    lineHeight: "20px"
  }),
  LabelSmall: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Regular,
    fontSize: 14,
    lineHeight: "18px"
  }),
  LabelXSmall: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Regular,
    fontSize: 12,
    lineHeight: "16px"
  }),
  Caption: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Regular,
    fontSize: 14,
    lineHeight: "20px"
  }),
  Footnote: _extends({}, common, {
    fontFamily: SansFamily,
    fontWeight: Regular,
    fontSize: 12,
    lineHeight: "18px"
  })
});

const _excluded$f = ["style", "children", "tag"];
const Title = React.forwardRef(function Title(_ref, ref) {
  let {
      style,
      children,
      tag = "h1"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$f);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.Title, style],
    ref: ref
  }), children);
});

const _excluded$e = ["style", "children", "tag"];
const HeadingLarge = React.forwardRef(function HeadingLarge(_ref, ref) {
  let {
      style,
      children,
      tag = "h2"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$e);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.HeadingLarge, style],
    ref: ref
  }), children);
});

const _excluded$d = ["style", "children", "tag"];
const HeadingMedium = React.forwardRef(function HeadingMedium(_ref, ref) {
  let {
      style,
      children,
      tag = "h3"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$d);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.HeadingMedium, style],
    ref: ref
  }), children);
});

const _excluded$c = ["style", "children", "tag"];
const HeadingSmall = React.forwardRef(function HeadingSmall(_ref, ref) {
  let {
      style,
      children,
      tag = "h4"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$c);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.HeadingSmall, style],
    ref: ref
  }), children);
});

const _excluded$b = ["style", "children", "tag"];
const HeadingXSmall = React.forwardRef(function HeadingXSmall(_ref, ref) {
  let {
      style,
      children,
      tag = "h4"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$b);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.HeadingXSmall, style],
    ref: ref
  }), children);
});

const _excluded$a = ["style", "children", "tag"];
const BodySerifBlock = React.forwardRef(function BodySerifBlock(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$a);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.BodySerifBlock, style],
    ref: ref
  }), children);
});

const _excluded$9 = ["style", "children", "tag"];
const BodySerif = React.forwardRef(function BodySerif(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$9);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.BodySerif, style],
    ref: ref
  }), children);
});

const _excluded$8 = ["style", "children", "tag"];
const BodyMonospace = React.forwardRef(function BodyMonospace(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$8);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.BodyMonospace, style],
    ref: ref
  }), children);
});

const _excluded$7 = ["style", "children", "tag"];
const Body = React.forwardRef(function Body(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$7);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.Body, style],
    ref: ref
  }), children);
});

const _excluded$6 = ["style", "children", "tag"];
const LabelLarge = React.forwardRef(function LabelLarge(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$6);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.LabelLarge, style],
    ref: ref
  }), children);
});

const _excluded$5 = ["style", "children", "tag"];
const LabelMedium = React.forwardRef(function LabelMedium(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$5);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.LabelMedium, style],
    ref: ref
  }), children);
});

const _excluded$4 = ["style", "children", "tag"];
const LabelSmall = React.forwardRef(function LabelSmall(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$4);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.LabelSmall, style],
    ref: ref
  }), children);
});

const _excluded$3 = ["style", "children", "tag"];
const LabelXSmall = React.forwardRef(function LabelXSmall(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$3);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.LabelXSmall, style],
    ref: ref
  }), children);
});

const _excluded$2 = ["style", "children", "tag"];
const Tagline = React.forwardRef(function Tagline(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.Tagline, style],
    ref: ref
  }), children);
});

const _excluded$1 = ["style", "children", "tag"];
const Caption = React.forwardRef(function Caption(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.Caption, style],
    ref: ref
  }), children);
});

const _excluded = ["style", "children", "tag"];
const Footnote = React.forwardRef(function Footnote(_ref, ref) {
  let {
      style,
      children,
      tag = "span"
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  return React.createElement(Text, _extends({}, otherProps, {
    tag: tag,
    style: [styles.Footnote, style],
    ref: ref
  }), children);
});

export { Body, BodyMonospace, BodySerif, BodySerifBlock, Caption, Footnote, HeadingLarge, HeadingMedium, HeadingSmall, HeadingXSmall, LabelLarge, LabelMedium, LabelSmall, LabelXSmall, Tagline, Title, styles };
